import { Card, Checkbox, Col, Divider, Image, Radio, RadioChangeEvent, Row } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// components
import FilePreview from 'components/filePreview/filePreview';
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';

// services

// styles

// models
import { UpdateDocumentRequestType, UpdateDocumentRequest } from '../models/updateDocument';
import { onGetUpdateDocumentsRequestById, onGetUpdateDocumentsRequestTypes } from '../services/updateDocumentsServices';
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { isRTL } from 'core/services/language';
import { formatFileSize } from 'utils/FileSize';
import DisabledElement from '../../../../../components/disabledElement/disabledElement';

interface UpdateDocumentViewPageProps {}

const UpdateDocumentViewPage: FunctionComponent<UpdateDocumentViewPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requestTypes, setRequestTypes] = useState<UpdateDocumentRequestType[]>([]);
	const [updateDocumentRequest, setUpdateDocumentRequest] = useState<UpdateDocumentRequest>();

	useEffect(() => {
		getDocumentUpdateRequest();
	}, []);

	const getDocumentUpdateRequest = async () => {
		try {
			setIsLoading(true);

			const availableRequestTypes = await onGetUpdateDocumentsRequestTypes();
			setRequestTypes(availableRequestTypes);

			const documentResponse = await onGetUpdateDocumentsRequestById(id as string);
			setUpdateDocumentRequest(documentResponse);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/update-documents');
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			<Card>
				<ComponentHeader title={t('ACTIONS.UPDATE_COMPANY_DOCUMENTS_DETAILS')} />

				{isLoading ? (
					<LoadingComponent />
				) : (
					<>
						<h6 className="mt-4">{t('ACTIONS.DOCUMENT_TYPE')}</h6>
						<Radio.Group
							disabled
							defaultValue={updateDocumentRequest?.document.key}
							className="customRadio"
							style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
							options={requestTypes.map((type) => ({
								value: type.key,
								label: <p className="fw-medium mb-0">{isRTL() ? type.translated_name.ar : type.translated_name.en}</p>,
							}))}
						/>

						<Divider />

						{requestTypes.find((type) => type.key === updateDocumentRequest?.document.key)?.has_integer_input && (
							<div className="mb-4">
								<DisabledElement label={t('ACTIONS.TAX_REGISTRATION_NUMBER')} value={updateDocumentRequest?.new_value} />
							</div>
						)}

						{requestTypes.find((type) => type.key === updateDocumentRequest?.document.key)?.has_translatable && (
							<Row gutter={[16, 16]}>
								<Col span={24} md={12}>
									<DisabledElement
										fullWidth
										label={t('ACTIONS.CHAIRMAN_NAME_AR')}
										value={updateDocumentRequest?.translated_document_name?.ar}
									/>
								</Col>

								<Col span={24} md={12}>
									<DisabledElement
										fullWidth
										label={t('ACTIONS.CHAIRMAN_NAME_EN')}
										value={updateDocumentRequest?.translated_document_name?.en}
									/>
								</Col>
							</Row>
						)}

						<FilePreview
							className="mt-3"
							name={
								(isRTL() ? updateDocumentRequest?.translated_document_name?.ar : updateDocumentRequest?.translated_document_name?.en) || ''
							}
							downloadLink={updateDocumentRequest?.new_document.download_link}
							data={formatFileSize(updateDocumentRequest?.new_document.size_in_kilobyte || 0)}
						/>

						{requestTypes.find((type) => type.key === updateDocumentRequest?.document.key)?.has_signature && (
							<div className="mt-4">
								<Image src={updateDocumentRequest?.signature_path?.download_link} width={200} height={200} />
								<p className="text-light fw-medium">{t('ACTIONS.CHAIRMAN_SIGNATURE_DESC')}</p>
							</div>
						)}

						{requestTypes.find((type) => type.key === updateDocumentRequest?.document.key)?.show_publish_at_platforms_checkbox && (
							<Checkbox className="mt-4" disabled checked={updateDocumentRequest?.publish_at_platforms}>
								{t('ACTIONS.SHOW_ON_PLATFORM_CONNECTED_TO_HEFTH')}
							</Checkbox>
						)}
					</>
				)}
			</Card>
		</>
	);
};

export default UpdateDocumentViewPage;
